import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { AdvertiserResponse, CreateAdvertiser } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

interface FormData {
  name: string;
  status: 'active' | 'inactive';
}

const initialFormState: FormData = {
  name: '',
  status: 'active',
};

export function Advertisers() {
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: toastError } = useToast();

  const loadAdvertisers = () => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadAdvertisers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data: CreateAdvertiser = {
        name: formData.name,
        status: formData.status,
      };

      if (editingId) {
        await api.updateAdvertiser(editingId, data);
        success('Advertiser updated successfully');
      } else {
        await api.createAdvertiser(data);
        success('Advertiser created successfully');
      }

      setIsDialogOpen(false);
      setFormData(initialFormState);
      setEditingId(null);
      loadAdvertisers();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to save advertiser');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (advertiser: AdvertiserResponse) => {
    setEditingId(advertiser.id);
    setFormData({
      name: advertiser.name,
      status: advertiser.status as 'active' | 'inactive',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await api.deleteAdvertiser(id);
      success('Advertiser deleted successfully');
      loadAdvertisers();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete advertiser');
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(initialFormState);
    setIsDialogOpen(true);
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading advertisers: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Advertisers</h1>
          <p className="text-muted-foreground mt-2">Manage your advertisers</p>
        </div>
        <Button onClick={openCreateDialog}>Create Advertiser</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Advertisers</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {advertisers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                    No advertisers found. Create your first advertiser to get started.
                  </TableCell>
                </TableRow>
              ) : (
                advertisers.map((advertiser) => (
                  <TableRow key={advertiser.id}>
                    <TableCell className="font-medium">{advertiser.name}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          advertiser.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {advertiser.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(advertiser)}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(advertiser.id, advertiser.name)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Advertiser' : 'Create Advertiser'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update the advertiser details below.'
                : 'Create a new advertiser by filling in the details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Acme Corporation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })
                  }
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
